// client/src/lib/queryClient.ts
// Shared React Query client for AGOC School.
//
// The queryClient singleton is created here so pages can import and invalidate
// queries directly (e.g. queryClient.invalidateQueries(['courses'])).
//
// NOTE: The old apiRequest() helper is kept for backwards compatibility with
// pages migrated from PilotAcademyAI. New code should use apiClient from
// @/lib/apiClient instead, which automatically attaches Firebase Bearer tokens.

import { QueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
    },
  },
});

/**
 * Legacy API helper kept for compatibility with migrated page components.
 * Makes a fetch() call WITHOUT Firebase auth. Only use for public endpoints.
 * Prefer apiClient.get/post/etc. for authenticated requests.
 *
 * @deprecated Use apiClient from @/lib/apiClient for authenticated calls.
 */
export async function apiRequest(
  method: string,
  url: string,
  data?: unknown,
): Promise<Response> {
  const res = await fetch(url, {
    method,
    headers: data ? { 'Content-Type': 'application/json' } : {},
    body: data ? JSON.stringify(data) : undefined,
  });

  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }

  return res;
}

// Re-export apiClient for convenience so pages can import both from here
export { apiClient };
